import { createContext, useContext } from 'react'

/**
 * ScreeningModeContext — the single seam that lets the SAME screening step
 * components run in two contexts:
 *
 *  - `live`  → /tenant/screening: posts persist to the DB, ReviewStep fires the
 *              real `/resources/screening-submit` pipeline, ID/Income hit Veriff
 *              and the persisting Flinks callback.
 *  - `demo`  → /demo: nothing is written. Step posts go to the demo route's
 *              no-op action, ReviewStep skips the network submit, and the ID /
 *              Income steps are rendered by simulated demo variants.
 *
 * The default value is `live`, so any real screening component that reads this
 * context without an explicit provider keeps today's exact behavior.
 */
export type ScreeningMode = 'live' | 'demo'

export interface ScreeningModeConfig {
	mode: ScreeningMode
	/**
	 * Where ReviewStep posts the final application.
	 *  - live → `/resources/screening-submit` (real pipeline)
	 *  - demo → `null` (no network; ReviewStep only runs its success UX)
	 */
	reviewSubmitAction: string | null
	/**
	 * Demo-only hook fired when the user clicks "Submit Application" on the
	 * review step. Lets the demo route transition to its report screen instead
	 * of hitting the real submission pipeline. Undefined in live mode.
	 */
	onReviewSubmitted?: () => void
}

export const LIVE_SCREENING_MODE: ScreeningModeConfig = {
	mode: 'live',
	reviewSubmitAction: '/resources/screening-submit',
}

export const DEMO_SCREENING_MODE: ScreeningModeConfig = {
	mode: 'demo',
	reviewSubmitAction: null,
}

const ScreeningModeContext =
	createContext<ScreeningModeConfig>(LIVE_SCREENING_MODE)

export function ScreeningModeProvider({
	value,
	children,
}: {
	value: ScreeningModeConfig
	children: React.ReactNode
}) {
	return (
		<ScreeningModeContext.Provider value={value}>
			{children}
		</ScreeningModeContext.Provider>
	)
}

export function useScreeningMode(): ScreeningModeConfig {
	return useContext(ScreeningModeContext)
}
